import {
  type RefObject, useCallback, useRef, useState,
} from 'react';
import type { Scrollbar } from 'react-scrollbars-custom';
import type { ScrollState } from 'react-scrollbars-custom/dist/types/types';
import type { ScrollbarProps } from '~/_shared/components/scrollbar/scrollbar.component';
import { useThrottle } from '~/_shared/utils/hooks/useThrottle';

const SCROLL_STEP = 3;
const SCROLL_TOLERANCE_PX = 1;

export function useScrollControls(scrollbarRef: RefObject<Scrollbar>, scrollStep = SCROLL_STEP) {
  const scrollAnimationFrameId = useRef<number | null>(null);
  const [atTop, setAtTop] = useState(true);
  const [atBottom, setAtBottom] = useState(false);
  const [scrollXPossible, setScrollXPossible] = useState(false);
  const [scrollYPossible, setScrollYPossible] = useState(false);

  const onUpdate: ScrollbarProps['onUpdate'] = useThrottle(() => {
    if (scrollbarRef.current) {
      const scrollState = scrollbarRef.current.getScrollState();
      setAtTop(isScrollbarAtTop(scrollState));
      setAtBottom(isScrollbarAtBottom(scrollState));
      setScrollXPossible(scrollState.scrollXPossible);
      setScrollYPossible(scrollState.scrollYPossible);
    }
  }, [scrollbarRef], 50);

  const continuousScroll = useCallback((direction: 'up' | 'down') => {
    if (!scrollbarRef.current) {
      return;
    }

    const currentScrollTop = scrollbarRef.current.scrollTop;
    const newScrollTop = direction === 'up'
      ? Math.max(0, currentScrollTop - scrollStep)
      : currentScrollTop + scrollStep;

    const currentScrollState = scrollbarRef.current.getScrollState();
    if (direction === 'up' && isScrollbarAtTop(currentScrollState)) {
      return;
    }

    if (direction === 'down' && isScrollbarAtBottom(currentScrollState)) {
      return;
    }

    scrollbarRef.current.scrollTo(undefined, newScrollTop);
    scrollAnimationFrameId.current = requestAnimationFrame(() => continuousScroll(direction));
  }, [scrollStep, scrollbarRef]);

  const stopScrolling = useCallback(() => {
    if (scrollAnimationFrameId.current !== null) {
      cancelAnimationFrame(scrollAnimationFrameId.current);
      scrollAnimationFrameId.current = null;
    }
  }, []);

  const scrollUp = useCallback(() => continuousScroll('up'), [continuousScroll]);
  const scrollDown = useCallback(() => continuousScroll('down'), [continuousScroll]);

  return {
    atBottom,
    atTop,
    scrollDown,
    scrollUp,
    scrollXPossible,
    scrollYPossible,
    stopScrolling,
    updateScrollBoundaries: onUpdate,
  };
}

const isScrollbarAtTop = (scrollState: ScrollState) =>
  scrollState.scrollTop <= SCROLL_TOLERANCE_PX;
const isScrollbarAtBottom = (scrollState: ScrollState) =>
  scrollState.scrollTop + scrollState.clientHeight + SCROLL_TOLERANCE_PX >= scrollState.scrollHeight;
